// src/utils/paymentCut.js
//
// Fecha en que CSR recibió cada pago (BofA / Zelle) y armado de los "cortes":
// el listado de lo cobrado en un rango de fechas, con las mismas columnas del
// estado que manda el PA (tipo, cliente, claim, cheque, monto, % PA, fee del
// PA, % CSR y total a pagar).
import { formatDateToYYYYMMDD } from './dateHelpers';

const pad = n => String(n).padStart(2, '0');
export const toIsoDay = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const todayIso = () => toIsoDay(new Date());

const timestampToIso = value => {
  if (!value) return '';
  if (typeof value.toDate === 'function') return toIsoDay(value.toDate());
  if (typeof value.seconds === 'number') return toIsoDay(new Date(value.seconds * 1000));
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : toIsoDay(parsed);
};

// Fecha del pago en YYYY-MM-DD. `receivedDate` es la que se anota a mano (la
// del depósito en BofA); si falta, se usa el día en que se registró en la app
// y `estimated` queda en true para avisarlo.
export const paymentReceivedDate = payment => {
  if (!payment) return { date: '', estimated: true };
  if (payment.receivedDate) return { date: formatDateToYYYYMMDD(payment.receivedDate), estimated: false };
  if (payment.date) return { date: formatDateToYYYYMMDD(payment.date), estimated: false };
  return { date: timestampToIso(payment.recordedAt || payment.paidAt), estimated: true };
};

// Pagos que cuentan como dinero recibido. Una entrada `backupOnly` es el
// comprobante de un pago registrado antes de existir el historial: si el
// cheque no tiene otros pagos, esa entrada ES el registro del pago y cuenta;
// si los tiene, es solo un respaldo extra y no se suma (evita contarlo doble).
export const countedPayments = (history = []) => {
  const regular = history.map((payment, index) => ({ payment, index })).filter(({ payment }) => payment && !payment.backupOnly);
  if (regular.length) return regular;
  return history.map((payment, index) => ({ payment, index })).filter(({ payment }) => payment?.backupOnly);
};

// Rango de "los últimos N días" contando hoy: 5 días = hoy y los 4 anteriores.
export const lastDaysRange = (days, now = new Date()) => {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { from: toIsoDay(from), to: toIsoDay(now) };
};

const inRange = (date, from, to) => Boolean(date) && (!from || date >= from) && (!to || date <= to);
const round2 = value => Math.round((Number(value) || 0) * 100) / 100;

const baseRow = check => {
  const amount = Number(check.amount) || 0;
  const paPct = Number(check.adjusterPercentage) || 0;
  const csrPct = Number(check.csrPercentage) || 0;
  const paFee = (amount * paPct) / 100;
  return {
    checkId: check.id,
    cause: check.cause || '',
    clientName: check.clientName || '',
    claimNumber: check.claimNumber || '',
    checkNumber: check.checkNumber || '',
    companyName: check.companyName || '',
    adjusterName: check.adjusterName || '',
    invoiceNumber: check.invoiceNumber || '',
    checkDate: formatDateToYYYYMMDD(check.checkDate || ''),
    amount,
    paPct,
    paFee: round2(paFee),
    csrPct,
    payable: round2((paFee * csrPct) / 100)
  };
};

// basis 'received': una fila por pago recibido dentro del rango (lo cobrado).
// basis 'check':    una fila por cheque con fecha de cheque en el rango, cobrado o no.
export const buildPaymentCut = (checks = [], { from = '', to = '', company = '', basis = 'received' } = {}) => {
  const rows = [];
  checks.forEach(check => {
    if (!check || check.deleted) return;
    if (company && (check.companyName || '') !== company) return;
    if (basis === 'check') {
      const row = baseRow(check);
      if (!inRange(row.checkDate, from, to)) return;
      const received = Number(check.deposit) || 0;
      rows.push({ ...row, key: check.id, date: row.checkDate, estimated: false, received: round2(received), paid: Boolean(check.paid) });
      return;
    }
    countedPayments(check.paymentHistory || []).forEach(({ payment, index }) => {
      const { date, estimated } = paymentReceivedDate(payment);
      if (!inRange(date, from, to)) return;
      rows.push({
        ...baseRow(check),
        key: `${check.id}-${index}`,
        paymentIndex: index,
        date,
        estimated,
        reference: payment.reference || '',
        received: round2(payment.amount),
        hasReceipt: Boolean(payment.backup?.storageKey),
        paid: Boolean(check.paid)
      });
    });
  });
  rows.sort((a, b) => a.date.localeCompare(b.date) || a.clientName.localeCompare(b.clientName));
  const totals = rows.reduce((acc, row) => ({
    amount: acc.amount + row.amount,
    paFee: acc.paFee + row.paFee,
    payable: acc.payable + row.payable,
    received: acc.received + row.received
  }), { amount: 0, paFee: 0, payable: 0, received: 0 });
  Object.keys(totals).forEach(key => { totals[key] = round2(totals[key]); });
  return { rows, totals, estimatedCount: rows.filter(row => row.estimated).length };
};

// Buscador de invoices (todas las fechas): por número de invoice (con o sin
// "INV-"), cliente, claim # o cheque #. Un número exacto de invoice va primero.
export const searchInvoices = (checks = [], query = '', limit = 25) => {
  const raw = String(query || '').trim().toLowerCase();
  if (!raw) return [];
  const q = raw.replace(/^inv-?\s*/, '');
  const matches = checks.filter(check => {
    if (!check || check.deleted) return false;
    const invoice = String(check.invoiceNumber || '').toLowerCase();
    return (invoice && invoice.includes(q))
      || [check.clientName, check.claimNumber, check.checkNumber, check.companyName]
        .some(value => String(value || '').toLowerCase().includes(raw));
  });
  const score = check => (String(check.invoiceNumber || '').toLowerCase() === q ? 0 : 1);
  return matches
    .sort((a, b) => score(a) - score(b) || (Number(b.invoiceNumber) || 0) - (Number(a.invoiceNumber) || 0))
    .slice(0, limit);
};
